import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import { Typography } from '@mui/material';

import MarkdownText from 'src/components/common/MarkdownText';
import Tooltip from 'src/components/common/Tooltip';
import GradientButton from 'src/components/GradientButton';
import IconButton from 'src/components/IconButton';
import TransactionBottomSheet from 'src/components/TransactionBottomSheet';
import TypeButton from 'src/components/TypeButton';
import { MINTSCAN_URL } from 'src/constants/explorer';
import { VOTE_OPTION } from 'src/constants/proposals';
import { DYDX_TOKEN } from 'src/constants/tokens';
import { useAccountSWR } from 'src/hooks/SWR/cosmos/useAccountSWR';
import { useCosmosBalanceSWR } from 'src/hooks/SWR/cosmos/useCosmosBalanceSWR';
import { useProposalsSWR } from 'src/hooks/SWR/cosmos/useProposalsSWR';
import { useSimulateSWR } from 'src/hooks/SWR/cosmos/useSimulateSWR';
import { useTxInfoSWR } from 'src/hooks/SWR/cosmos/useTxInfoSWR';
import { useCurrentAccount } from 'src/hooks/useAccount';
import { getSimulateVoteTxBytes, getVoteDirectDoc, signDirectTx } from 'src/utils/cosmos';
import { mnemonicToPair } from 'src/utils/crypto';
import { governanceClickEvent, pageView } from 'src/utils/ga';
import { ceil, gt, times, toDisplayDenomAmount } from 'src/utils/number';
import { formatDate } from 'src/utils/string';

import {
  ArrowIconContainer,
  Container,
  ContentsContainer,
  DetailContainer,
  Header,
  HeaderTitle,
  ProposalTitleContainer,
  ProposalTitleWrapperContainer,
  StyledBottomSheet,
  VoteBodyContainer,
  VoteBodyDescriptionContainer,
  VoteBodyDescriptionShowButton,
  VoteContainer,
  VoteOptionButtonContainer,
  VoteTopContainer,
  WrapperContainer,
} from './styled';
import { Base02TextContainer } from '../ProposalButton/styled';
import SignConfirmBottomSheet from '../SignConfirmBottomSheet';
import TxDetails from '../TxDetails';

import { ReactComponent as RightArrowIcon } from 'src/assets/svg/BlueRightArrow.svg';
import { ReactComponent as CloseIcon } from 'src/assets/svg/Close.svg';

type VoteBottomSheetProps = Omit<React.ComponentProps<typeof StyledBottomSheet>, 'children'> & {
  selectedProposalId: string;
};

export default function VoteBottomSheet({ selectedProposalId, onClose, ...remainder }: VoteBottomSheetProps) {
  const { enqueueSnackbar } = useSnackbar();

  const { currentAccount } = useCurrentAccount();

  const [isDisabled, setIsDisabled] = useState(false);

  const [isOpenSignConfirmBottomsheet, setIsOpenSignConfirmBottomsheet] = useState(false);
  const [isOpenTrasactionBottomsheet, setIsOpenTrasactionBottomsheet] = useState(false);

  const [isTxTrackingFailed, setIsTxTrackingFailed] = useState(false);
  const [isTxTrackingSuccess, setIsTxTrackingSucces] = useState(false);

  const [selectedVoteOption, setSelectedVoteOption] = useState('');

  const [currentMemo, setCurrentMemo] = useState('');

  const [transactionHash, setTransactionHash] = useState('');

  const [debouncedCurrentMemo] = useDebounce(currentMemo, 500);

  const proposals = useProposalsSWR({ proposalStatus: '0' });

  const currentProposal = useMemo(
    () => proposals.data?.proposals?.find((item) => item.id === selectedProposalId),
    [proposals.data?.proposals, selectedProposalId],
  );

  const votingEndTime = useMemo(() => formatDate(new Date(currentProposal?.voting_end_time || '')), [currentProposal?.voting_end_time]);

  const balance = useCosmosBalanceSWR(currentAccount.cosmosAddress);
  const baseCoinAmount = useMemo(() => balance.data?.balances?.find((item) => item.denom === DYDX_TOKEN.denom)?.amount || '0', [balance.data?.balances]);

  const account = useAccountSWR(currentAccount.cosmosAddress);

  const simulateDirectTxBytes = useMemo(
    () =>
      account.data?.account.account_number && currentAccount.mnemonic && account.data?.account.sequence && currentProposal && selectedVoteOption
        ? getSimulateVoteTxBytes({
            accountNumber: account.data.account.account_number,
            sequence: account.data.account.sequence,
            mnemonic: currentAccount.mnemonic,
            voterAddress: currentAccount.cosmosAddress,
            proposalId: currentProposal.id,
            option: Number(selectedVoteOption),
            memo: debouncedCurrentMemo,
          })
        : undefined,
    [
      account.data?.account.account_number,
      account.data?.account.sequence,
      currentAccount.cosmosAddress,
      currentAccount.mnemonic,
      currentProposal,
      debouncedCurrentMemo,
      selectedVoteOption,
    ],
  );

  const simulate = useSimulateSWR({ txBytes: simulateDirectTxBytes?.tx_bytes });

  const simulatedGas = useMemo(
    () => (simulate.data?.gas_info?.gas_used ? String(ceil(times(simulate.data.gas_info.gas_used, '1.2'))) : undefined),
    [simulate.data?.gas_info?.gas_used],
  );

  const currentGas = useMemo(() => simulatedGas || '200000', [simulatedGas]);

  const baseEstimateFeeAmount = useMemo(() => String(ceil(times(currentGas, '12500000000'))), [currentGas]);

  const displayEstimateFeeAmount = useMemo(() => toDisplayDenomAmount(baseEstimateFeeAmount, DYDX_TOKEN.decimals), [baseEstimateFeeAmount]);

  const voteDirectTx = useMemo(
    () =>
      account.data?.account.account_number &&
      currentAccount.mnemonic &&
      account.data?.account.sequence &&
      currentAccount.cosmosAddress &&
      baseEstimateFeeAmount &&
      currentProposal &&
      selectedVoteOption &&
      currentGas
        ? getVoteDirectDoc({
            accountNumber: account.data.account.account_number,
            sequence: account.data.account.sequence,
            mnemonic: currentAccount.mnemonic,
            voterAddress: currentAccount.cosmosAddress,
            proposalId: currentProposal.id,
            option: Number(selectedVoteOption),
            simulatedGas: currentGas,
            simulatedFeeAmount: baseEstimateFeeAmount,
            memo: debouncedCurrentMemo,
          })
        : undefined,
    [
      account.data?.account.account_number,
      account.data?.account.sequence,
      baseEstimateFeeAmount,
      currentAccount.cosmosAddress,
      currentAccount.mnemonic,
      currentGas,
      currentProposal,
      debouncedCurrentMemo,
      selectedVoteOption,
    ],
  );
  const txInfo = useTxInfoSWR(transactionHash);

  const transactionBodyText = useMemo(() => {
    if (isTxTrackingFailed) return 'Voting Failed';

    if (isTxTrackingSuccess) return 'Voting Complete';

    return `Voting...`;
  }, [isTxTrackingFailed, isTxTrackingSuccess]);

  const transactionSubText = useMemo(() => (isTxTrackingSuccess ? `You can check your transaction status on Mintscan.` : undefined), [isTxTrackingSuccess]);

  const errorMessage = useMemo(() => {
    if (!currentAccount.cosmosAddress) {
      return 'Please connect wallet';
    }

    if (!selectedVoteOption) {
      return 'Please select vote option';
    }

    if (gt(baseEstimateFeeAmount, baseCoinAmount)) {
      return 'Insufficient DYDX balance for transaction fee';
    }

    if (!voteDirectTx) {
      return 'Invalid Transaction';
    }

    return '';
  }, [baseCoinAmount, baseEstimateFeeAmount, currentAccount.cosmosAddress, selectedVoteOption, voteDirectTx]);

  const txConfirmedStatus = useMemo(() => {
    if (txInfo.error?.response?.status && txInfo.error.response.status >= 400 && txInfo.error.response.status < 500) return 'pending';

    if (txInfo.data?.tx_response.code !== undefined) {
      if (txInfo.data.tx_response.code !== 0) return 'failed';

      if (txInfo.data.tx_response.code === 0) return 'confirmed';
    }

    return undefined;
  }, [txInfo.data?.tx_response.code, txInfo.error?.response?.status]);

  useEffect(() => {
    pageView('Vote', 'Vote');
  }, []);

  const debouncedEnabled = useDebouncedCallback(() => {
    setTimeout(() => {
      setIsDisabled(false);
    }, 700);
  }, 700);

  useEffect(() => {
    setIsDisabled(true);

    debouncedEnabled();
  }, [debouncedEnabled, voteDirectTx, currentMemo]);

  useEffect(() => {
    if (isOpenTrasactionBottomsheet) {
      if (txConfirmedStatus === 'confirmed') {
        setIsTxTrackingSucces(true);
      }
      if (txConfirmedStatus === 'failed' || txInfo.hasTimedOut) {
        setIsTxTrackingFailed(true);
      }
    }
  }, [isOpenTrasactionBottomsheet, txConfirmedStatus, txInfo.hasTimedOut]);

  useEffect(() => {
    if (!currentAccount.cosmosAddress) {
      setIsOpenTrasactionBottomsheet(false);
      setIsTxTrackingFailed(false);
      setIsTxTrackingSucces(false);
      setTransactionHash('');
      setSelectedVoteOption('');
      setCurrentMemo('');
    }
  }, [currentAccount.cosmosAddress, onClose]);

  return (
    <StyledBottomSheet {...remainder} onClose={onClose}>
      <Container>
        <>
          <ContentsContainer>
            <Header>
              <HeaderTitle>
                <Typography variant="h2">Vote</Typography>
              </HeaderTitle>
              <IconButton
                onClick={() => {
                  setSelectedVoteOption('');
                  onClose?.({}, 'backdropClick');
                }}
              >
                <CloseIcon />
              </IconButton>
            </Header>

            <VoteContainer>
              <>
                <VoteTopContainer>
                  <ProposalTitleWrapperContainer>
                    <Typography variant="h4">{`#${currentProposal?.id || ''}. `}</Typography>
                    &nbsp;
                    <ProposalTitleContainer>
                      <Typography variant="h5">{` ${currentProposal?.title || ''}`}</Typography>
                    </ProposalTitleContainer>
                  </ProposalTitleWrapperContainer>
                  <Base02TextContainer>
                    <Typography variant="h9">{votingEndTime}</Typography>
                  </Base02TextContainer>
                </VoteTopContainer>

                <VoteBodyContainer>
                  <VoteBodyDescriptionContainer>
                    <MarkdownText>{currentProposal?.summary}</MarkdownText>
                    <VoteBodyDescriptionShowButton type="button" onClick={() => window.open(`${MINTSCAN_URL}/proposals/${currentProposal?.id || ''}` || '')}>
                      <Typography variant="h10">Show Full Description</Typography>
                      <ArrowIconContainer>
                        <RightArrowIcon />
                      </ArrowIconContainer>
                    </VoteBodyDescriptionShowButton>
                  </VoteBodyDescriptionContainer>
                  <VoteOptionButtonContainer>
                    <TypeButton
                      text="YES"
                      onClick={() => {
                        setSelectedVoteOption(VOTE_OPTION.VOTE_OPTION_YES);
                      }}
                      isActive={selectedVoteOption === VOTE_OPTION.VOTE_OPTION_YES}
                    />
                    <TypeButton
                      text="NO"
                      onClick={() => {
                        setSelectedVoteOption(VOTE_OPTION.VOTE_OPTION_NO);
                      }}
                      isActive={selectedVoteOption === VOTE_OPTION.VOTE_OPTION_NO}
                    />

                    <TypeButton
                      text="VETO"
                      onClick={() => {
                        setSelectedVoteOption(VOTE_OPTION.VOTE_OPTION_NO_WITH_VETO);
                      }}
                      isActive={selectedVoteOption === VOTE_OPTION.VOTE_OPTION_NO_WITH_VETO}
                    />
                    <TypeButton
                      text="ABSTAIN"
                      onClick={() => {
                        setSelectedVoteOption(VOTE_OPTION.VOTE_OPTION_ABSTAIN);
                      }}
                      isActive={selectedVoteOption === VOTE_OPTION.VOTE_OPTION_ABSTAIN}
                    />
                  </VoteOptionButtonContainer>
                </VoteBodyContainer>
              </>
            </VoteContainer>

            <DetailContainer>
              <TxDetails estimatedGasFee={displayEstimateFeeAmount} />
            </DetailContainer>
          </ContentsContainer>
          <Tooltip varient="error" title={errorMessage} placement="top" arrow>
            <div>
              <GradientButton
                disabled={!!errorMessage || !voteDirectTx}
                onClick={() => {
                  setIsOpenSignConfirmBottomsheet(true);
                }}
              >
                <Typography variant="h4">NEXT</Typography>
              </GradientButton>
            </div>
          </Tooltip>
        </>
      </Container>

      <WrapperContainer data-is-open={isOpenSignConfirmBottomsheet}>
        <SignConfirmBottomSheet
          open={isOpenSignConfirmBottomsheet}
          currentProposal={currentProposal}
          variant="persistent"
          title="Voting Details"
          descriptionText={`Review your voting decision and click 'Confirm'.`}
          voteOption={selectedVoteOption}
          displayEstimatedGasFeeAmount={displayEstimateFeeAmount}
          currentMemo={currentMemo}
          isButtonDisabled={isDisabled}
          isEstimation={!simulatedGas && !simulate.error}
          setCurrentMemo={(memo) => {
            setCurrentMemo(memo);
          }}
          transitionDuration={0}
          onClickConfirm={async () => {
            try {
              if (!voteDirectTx || !account.data?.account.account_number) return;
              setIsOpenSignConfirmBottomsheet(false);
              setIsOpenTrasactionBottomsheet(true);

              const keypair = mnemonicToPair(currentAccount.mnemonic);
              const response = await signDirectTx(voteDirectTx, keypair, account.data.account.account_number);

              governanceClickEvent('Governance', 'Governance');

              setTransactionHash(response.tx_response.txhash);

              enqueueSnackbar('Transaction broadcasting success', {
                variant: 'success',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                txHash: response.tx_response.txhash,
                isCosmosTx: true,
              });

              await txInfo.mutate();
            } catch (e) {
              setIsTxTrackingFailed(true);

              enqueueSnackbar('Transaction Failed', {
                variant: 'error',
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                errorMsg: '',

                autoHideDuration: 3000,
              });
            }
          }}
          onClose={() => {
            setIsOpenSignConfirmBottomsheet(false);
            setCurrentMemo('');
          }}
        />
      </WrapperContainer>

      <WrapperContainer data-is-open={isOpenTrasactionBottomsheet}>
        <TransactionBottomSheet
          open={isOpenTrasactionBottomsheet}
          variant="persistent"
          title="Voting Status"
          bodyText={transactionBodyText}
          subText={transactionSubText}
          // redirectButtonText={transactionRedirectText}
          isFailed={isTxTrackingFailed}
          isSuccess={isTxTrackingSuccess}
          isStaking
          txHash={transactionHash}
          transitionDuration={0}
          onClickRedirect={() => {
            window.open('https://medium.com/cosmostation/dydx-wallet-migration-guide-how-to-manage-your-dydx-tokens-43982ffddfb5');
          }}
          onClose={() => {
            setIsOpenTrasactionBottomsheet(false);
            setIsTxTrackingFailed(false);
            setIsTxTrackingSucces(false);
            setTransactionHash('');
            setCurrentMemo('');
            setSelectedVoteOption('');
            onClose?.({}, 'backdropClick');
          }}
        />
      </WrapperContainer>
    </StyledBottomSheet>
  );
}
